import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import api, { getWilayas, getCommunesOfWilaya } from '../../utils/Api'
import {
  CheckCircle,
  Circle,
  ChevronRight,
  ChevronLeft,
  Plus,
  Trash2,
  Upload,
  User,
  Building,
  Globe,
  Linkedin,
  Facebook,
  Instagram,
  Loader2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import ImageCropperModal from "../../components/common/ImageCropperModal";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const RecruiterCompleteProfile = () => {
  const dispatch = useDispatch();
  const { user, token, loading } = useSelector((state) => state.auth);
  const isAuthenticated = !!token && !!user;
  const [currentStep, setCurrentStep] = useState(0);
  const [sectors, setSectors] = useState([]);
  const navigate = useNavigate()
  const [wilayas, setWilayas] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [showWilayaDropdown, setShowWilayaDropdown] = useState(false);
  const [wilayaDisplayName, setWilayaDisplayName] = useState("");

  const [formData, setFormData] = useState({

    company_name: "",
    description: "",
    sector_id: "",
    custom_sector: "",
    website: "",
    phone_number: "",
    address: {
      line1: "",
      line2: "",
      zip_code: "",
      city: "",
      wilaya: "",
      coordinates: [],
    },
    social_links: {
      linkedin: "",
      facebook: "",
      instagram: "",
    },
    profile_picture: null,
    profile_picture_filename: "",
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const steps = [
    {
      id: "company_info",
      title: "Informations de l'entreprise",
      icon: Building,
      fields: [
        "company_name",
        "description",
        "sector_id",
        "website",
        "phone_number",
        "address",
      ],
    },
    {
      id: "social_links",
      title: "Réseaux sociaux",
      icon: Globe,
      fields: ["social_links"],
    },
    {
      id: "profile_picture",
      title: "Photo de profil",
      icon: User,
      fields: ["profile_picture"],
    },
  ];

  useEffect(() => {

    const fetchSectors = async () => {
      await api.get("/sector") // adapte l’URL selon ta route backend
        .then((res) => setSectors(res.data))
        .catch((err) =>
          console.error("Erreur lors de la récupération des secteurs :", err)
        );
    };

    fetchSectors()
    // console.log(sectors)
  }, []);

  const loadAvatarFromServer = async (profilePicturePath) => {
    if (!profilePicturePath) return;
    try {
      const pathParts = profilePicturePath.split('/');
      if (pathParts.length < 2) return;
      const folder = pathParts[0];
      const filename = pathParts[1];
      const response = await api.get(`/files/${folder}/${filename}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
      const url = window.URL.createObjectURL(blob);
      setAvatarPreview(url);
    } catch (error) {
      console.error("Error loading avatar:", error);
    }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!isAuthenticated) {
        setError("Utilisateur non authentifié. Veuillez vous reconnecter.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE_URL}/recruiter/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          if (response.status === 404) {
            // console.log(
            //   "Aucun profil recruteur trouvé, démarrage d'un nouveau profil."
            // );
            dispatch({ type: "USER_PROFILE_NOT_FOUND" });
            setCurrentStep(0);
            setFormData({
              company_name: "",
              description: "",
              sector_id: "",
              website: "",
              phone_number: "",
              address: {
                line1: "",
                line2: "",
                zip_code: "",
                city: "",
                city_id: "",
                wilaya: "",
                wilaya_id: "",
                coordinates: [],
              },
              social_links: { linkedin: "", facebook: "", instagram: "" },
              profile_picture: null,
              profile_picture_filename: "",
            });
          } else {
            const errorData = await response.json();
            throw new Error(
              errorData.error ||
              `Erreur ${response.status} lors de la récupération du profil.`
            );
          }
        } else {
          const profileData = await response.json();
          if (profileData.recruiter) {
            const { recruiter } = profileData;
            setFormData((prev) => ({
              ...prev,
              company_name: recruiter.company_name || "",
              description: recruiter.description || "",
              sector_id: recruiter.sector?._id || (recruiter.custom_sector ? "Autre" : ""),
              custom_sector: recruiter.custom_sector || "",
              website: recruiter.website || "",
              phone_number: recruiter.phone_number || "",
              address: {
                line1: recruiter.address?.line1 || "",
                line2: recruiter.address?.line2 || "",
                zip_code: recruiter.address?.zip_code || "",
                city: recruiter.address?.city || "",
                wilaya: recruiter.address?.wilaya || "",
                coordinates: recruiter.address?.coordinates || [],
              },
              social_links: recruiter.social_links || {
                linkedin: "",
                facebook: "",
                instagram: "",
              },
              profile_picture: recruiter.profile_picture || "",
            }));

            if (recruiter.profile_picture) {
              loadAvatarFromServer(recruiter.profile_picture);
            }


            // Pre-fetch communes if wilaya exists (it's an ID now)
            if (recruiter.address?.wilaya) {
              try {
                const wilayaRes = await getWilayas(recruiter.address.wilaya);
                const matchedWilaya = wilayaRes.find(w =>
                  w.id === recruiter.address.wilaya ||
                  w.code === recruiter.address.wilaya ||
                  w.name_fr.toLowerCase() === recruiter.address.wilaya.toLowerCase() ||
                  w.name_ar === recruiter.address.wilaya ||
                  (w.name && w.name.toLowerCase() === recruiter.address.wilaya.toLowerCase())
                );

                if (matchedWilaya) {
                  setWilayaDisplayName(matchedWilaya.name_fr || matchedWilaya.name);
                  const communesRes = await getCommunesOfWilaya(matchedWilaya.code);
                  setCommunes(communesRes);
                } else {
                  setWilayaDisplayName(recruiter.address.wilaya);
                }
              } catch (e) {
                console.error("Error fetching location data", e);
                setWilayaDisplayName(recruiter.address.wilaya);
              }
            }

            dispatch({
              type: "USER_PROFILE_LOADED",
              payload: {
                ...user,
                id: recruiter.user_id?._id || user.id,
                firstName: recruiter.first_name,
                lastName: recruiter.last_name,
                email: recruiter.user_id?.email,
                role: recruiter.user_id?.role,
                steps: recruiter.steps || {},
                is_profile_complete: recruiter.is_profile_complete || false,
                profile_picture: recruiter.profile_picture,
              },
            });

            let initialStep = 0;
            if (
              recruiter.steps &&
              typeof recruiter.steps === "object" &&
              Object.keys(recruiter.steps).length > 0
            ) {
              for (let i = 0; i < steps.length; i++) {
                if (!recruiter.steps[steps[i].id]) {
                  initialStep = i;
                  break;
                }
                if (i === steps.length - 1 && recruiter.steps[steps[i].id]) {
                  initialStep = steps.length - 1;
                  if (recruiter.is_profile_complete) {
                    //   console.log("Profil déjà complété.");
                  }
                }
              }
            }
            setCurrentStep(initialStep);
          } else {
            // console.log(
            //   "Profil non trouvé ou vide, démarrage d'un nouveau profil."
            // );
            dispatch({ type: "USER_PROFILE_NOT_FOUND" });
            setCurrentStep(0);
            setFormData({
              company_name: "",
              description: "",
              sector_id: "",
              custom_sector: "",
              website: "",
              phone_number: "",
              address: {
                line1: "",
                line2: "",
                zip_code: "",
                city: "",
                city_id: "",
                wilaya: "",
                wilaya_id: "",
                coordinates: [],
              },
              social_links: { linkedin: "", facebook: "", instagram: "" },
              profile_picture: null,
              profile_picture_filename: "",
            });
          }
        }
      } catch (err) {
        console.error("Erreur fetchProfileData:", err);
        setError("Une erreur est survenue lors du chargement du profil.");
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.id && isAuthenticated) {
      fetchProfileData();
    } else if (!isAuthenticated) {
      setError(
        "Session utilisateur non trouvée ou invalide. Veuillez vous reconnecter."
      );
      setIsLoading(false);
    }
  }, [user?.id, isAuthenticated, dispatch]);

  const handleInputChange = (field, value, index = null, subField = null) => {
    setFormData((prev) => {
      const newData = { ...prev };
      if (field === "profile_picture") {
        if (value && typeof value !== 'string') {
          // It's a file from cropper
          newData.profile_picture = value;
          newData.profile_picture_filename = value.name || "avatar.jpg";
        } else {
          newData.profile_picture = value;
          newData.profile_picture_filename = value ? value.split('/').pop() : "";
        }
      } else if (subField) {
        if (!newData[field]) newData[field] = {};
        newData[field] = { ...newData[field], [subField]: value };
      } else {
        newData[field] = value;
      }
      return newData;
    });
    setError(null);
  };

  const saveStepData = async (stepIndexToSave) => {
    if (!isAuthenticated) {
      setError("Action non autorisée. Session invalide.");
      return false;
    }

    const currentStepConfig = steps[stepIndexToSave];
    let payload;
    let apiEndpoint;
    let headers = { Authorization: `Bearer ${token}` };
    let body;

    setIsLoading(true);
    setError(null);

    switch (currentStepConfig.id) {
      case "company_info":
        payload = {
          company_name: formData.company_name,
          description: formData.description,
          sector_id: formData.sector_id,
          custom_sector: formData.sector_id === 'Autre' ? formData.custom_sector : undefined,
          website: formData.website,
          phone_number: formData.phone_number,
          address: formData.address,
        };
        apiEndpoint = `${API_BASE_URL}/recruiter/profile/company-info`;
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(payload);
        break;
      case "social_links":
        payload = { social_links: formData.social_links };
        apiEndpoint = `${API_BASE_URL}/recruiter/profile/social-links`;
        headers["Content-Type"] = "application/json";
        body = JSON.stringify(payload);
        break;
      case "profile_picture":
        if (formData.profile_picture) {
          const pictureFormData = new FormData();
          pictureFormData.append("profile_picture", formData.profile_picture);
          apiEndpoint = `${API_BASE_URL}/recruiter/profile/upload-picture`;
          body = pictureFormData;
        } else if (isStepCompleted(stepIndexToSave)) {
          setIsLoading(false);
          return true;
        } else {
          setError("Veuillez sélectionner une photo de profil.");
          setIsLoading(false);
          return false;
        }
        break;
      default:
        setError(`Étape inconnue: ${currentStepConfig.id}`);
        setIsLoading(false);
        return false;
    }

    try {
      if (
        body === undefined &&
        currentStepConfig.id === "profile_picture" &&
        isStepCompleted(stepIndexToSave)
      ) {
        setIsLoading(false);
        return true;
      }

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: headers,
        body: body,
      });
      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(
          responseData.error ||
          `Erreur lors de la sauvegarde: ${currentStepConfig.title}`
        );
      }

      console.log(
        `${currentStepConfig.title} sauvegardé avec succès:`,
        responseData
      );

      if (responseData.recruiter) {
        dispatch({
          type: "USER_PROFILE_STEP_SAVED",
          payload: {
            steps: responseData.recruiter.steps || user.steps,
            is_profile_complete:
              responseData.recruiter.is_profile_complete !== undefined
                ? responseData.recruiter.is_profile_complete
                : user.is_profile_complete,
            profile_picture:
              responseData.recruiter.profile_picture || user.profile_picture,
          },
        });

        if (
          currentStepConfig.id === "profile_picture" &&
          responseData.recruiter.profile_picture
        ) {
          setFormData((prev) => ({
            ...prev,
            profile_picture_filename: responseData.recruiter.profile_picture
              .split(/[\\/]/)
              .pop(),
            profile_picture: null,
          }));
          loadAvatarFromServer(responseData.recruiter.profile_picture);
        }
      }
      return true;
    } catch (err) {
      console.error(`Erreur saveStepData (${currentStepConfig.title}):`, err);
      setError(
        err.message ||
        `Une erreur est survenue lors de la sauvegarde de ${currentStepConfig.title}.`
      );
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    if (!canProceedToNext()) {
      setError("Veuillez remplir tous les champs requis pour continuer.");
      return;
    }
    if (currentStep < steps.length - 1) {
      const success = await saveStepData(currentStep);
      if (success) {
        setCurrentStep(currentStep + 1);
        setError(null);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!canProceedToNext()) {
      setError("Veuillez remplir tous les champs requis pour terminer.");
      return;
    }
    const success = await saveStepData(currentStep);
    if (success) {
      alert("Profil complété avec succès!");
      setTimeout(() => {
        window.location.reload();
      }, 0);
    }
  };

  const handleFinalSubmit = async () => {
    await handleSubmit()
    await api.post("/recruiter/is-complete") // adapte l’URL selon ta route backend
      //    .then((res) => )
      .catch((err) =>
        console.error("Erreur lors de la récupération des secteurs :", err)
      );
  }

  const isStepCompleted = useCallback(
    (stepIndex) => {
      if (!user || !user.steps || typeof user.steps !== "object") return false;
      const stepId = steps[stepIndex]?.id;
      if (!stepId) return false;
      return !!user.steps[stepId];
    },
    [user, steps]
  );

  const handleWilayaChange = async (e) => {
    const value = e.target.value;
    setWilayaDisplayName(value);

    // Reset stored ID
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, wilaya: '', city: '', zip_code: '', coordinates: [] }
    }));
    setCommunes([]);

    if (value.length > 0) {
      try {
        const res = await getWilayas(value);
        setWilayas(res);
        setShowWilayaDropdown(true);
      } catch (error) {
        console.error("Error fetching wilayas:", error);
      }
    } else {
      setWilayas([]);
      setShowWilayaDropdown(false);
    }
  };

  const selectWilaya = async (wilaya) => {
    setWilayaDisplayName(wilaya.name_fr || wilaya.name);
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        wilaya: wilaya.id,
        city: '',
        zip_code: '',
        coordinates: [Number(wilaya.latitude), Number(wilaya.longitude)]
      }
    }));
    setWilayas([]);
    setShowWilayaDropdown(false);

    try {
      const res = await getCommunesOfWilaya(wilaya.code);
      setCommunes(res);
    } catch (error) {
      console.error("Error fetching communes:", error);
    }
  };

  const handleCitySelect = (e) => {
    const cityId = e.target.value;
    const selectedCommune = communes.find(c => c.id === cityId);

    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        city: cityId,
        zip_code: selectedCommune ? selectedCommune.postalCode : '',
        coordinates: selectedCommune ? [Number(selectedCommune.latitude), Number(selectedCommune.longitude)] : prev.address.coordinates
      }
    }));
  };

  const canProceedToNext = () => {
    if (isLoading) return false;

    const currentStepConfig = steps[currentStep];
    if (!currentStepConfig) return false;

    switch (currentStepConfig.id) {
      case "company_info":
        return (
          formData.company_name &&
          (formData.sector_id === "Autre" ? formData.custom_sector : formData.sector_id) &&
          formData.address.wilaya
        );
      case "social_links":
        return true;
      case "profile_picture":
        if (isStepCompleted(currentStep) && !formData.profile_picture)
          return true;
        return formData.profile_picture !== null;
      default:
        return true;
    }
  };

  const renderCompanyInfo = () => (
    <div className="space-y-6">
      <div>
        <label
          htmlFor="company_name"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Nom de l'entreprise*
        </label>
        <input
          id="company_name"
          type="text"
          value={formData.company_name}
          onChange={(e) => handleInputChange("company_name", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      </div>
      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Description
        </label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      </div>
      <div>
        <label htmlFor="sector_id" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Secteur*</label>
        <select
          id="sector_id"
          value={formData.sector_id}
          onChange={(e) => {
            const val = e.target.value;
            handleInputChange('sector_id', val);
            if (val !== 'Autre') {
              handleInputChange('custom_sector', '');
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          <option value="">-- Choisir un secteur --</option>
          {sectors?.map((sector) => (
            <option key={sector._id} value={sector._id}>
              {sector.name}
            </option>
          ))}
          <option value="Autre">Autre</option>
        </select>
      </div>

      {formData.sector_id === 'Autre' && (
        <div className="animate-in slide-in-from-top-2 duration-200">
          <label
            htmlFor="custom_sector"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Précisez le secteur *
          </label>
          <input
            id="custom_sector"
            type="text"
            value={formData.custom_sector}
            onChange={(e) => handleInputChange("custom_sector", e.target.value)}
            placeholder="Tapez votre secteur"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
      )}

      <div>
        <label
          htmlFor="website"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Site Web
        </label>
        <input
          id="website"
          type="text"
          value={formData.website}
          onChange={(e) => handleInputChange("website", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      </div>
      <div>
        <label
          htmlFor="phone_number"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Numéro de téléphone
        </label>
        <input
          id="phone_number"
          type="tel"
          value={formData.phone_number}
          onChange={(e) => handleInputChange("phone_number", e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        />
      </div>
      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
          Adresse
        </h4>
        <div>
          <label
            htmlFor="address_line1"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Ligne 1
          </label>
          <input
            id="address_line1"
            type="text"
            value={formData.address.line1}
            onChange={(e) =>
              handleInputChange("address", e.target.value, null, "line1")
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div>
          <label
            htmlFor="address_line2"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Ligne 2 (optionnel)
          </label>
          <input
            id="address_line2"
            type="text"
            value={formData.address.line2}
            onChange={(e) =>
              handleInputChange("address", e.target.value, null, "line2")
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Wilaya*
            </label>
            <div className="relative">
              <input
                type="text"
                value={wilayaDisplayName}
                onChange={handleWilayaChange}
                placeholder="Rechercher une wilaya..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              {showWilayaDropdown && wilayas.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                  {wilayas.map((wilaya) => (
                    <button
                      key={wilaya.code}
                      type="button"
                      onClick={() => selectWilaya(wilaya)}
                      className="w-full px-4 py-2 text-left hover:bg-primary-50 dark:hover:bg-primary-900/20 transition flex items-center justify-between text-sm text-gray-900 dark:text-white"
                    >
                      {wilaya.code} - {wilaya.name_fr || wilaya.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ville
            </label>
            <select
              value={formData.address.city}
              onChange={handleCitySelect}
              disabled={!formData.address.wilaya || communes.length === 0}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50"
            >
              <option value="">Sélectionnez une ville</option>
              {communes.map((commune) => (
                <option key={commune.id || Math.random()} value={commune.id}>
                  {commune.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Code Postal
            </label>
            <input
              type="text"
              value={formData.address.zip_code}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-gray-100 dark:bg-gray-600 dark:border-gray-600 dark:text-white cursor-not-allowed"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSocialLinks = () => (
    <div className="space-y-6">
      <div>
        <label
          htmlFor="linkedin"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          LinkedIn
        </label>
        <input
          id="linkedin"
          type="text"
          value={formData.social_links.linkedin}
          onChange={(e) =>
            handleInputChange("social_links", e.target.value, null, "linkedin")
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="https://linkedin.com/company/..."
        />
      </div>
      <div>
        <label
          htmlFor="facebook"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Facebook
        </label>
        <input
          id="facebook"
          type="text"
          value={formData.social_links.facebook}
          onChange={(e) =>
            handleInputChange("social_links", e.target.value, null, "facebook")
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="https://facebook.com/company/..."
        />
      </div>
      <div>
        <label
          htmlFor="instagram"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          Instagram
        </label>
        <input
          id="instagram"
          type="text"
          value={formData.social_links.instagram}
          onChange={(e) =>
            handleInputChange("social_links", e.target.value, null, "instagram")
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          placeholder="https://instagram.com/company/..."
        />
      </div>
    </div>
  );

  const renderProfilePicture = () => (
    <div className="space-y-6">
      <div className="flex flex-col items-center">
        <div className="relative group">
          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white dark:border-gray-800 shadow-xl bg-gray-100 dark:bg-gray-700">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Profile Preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-16 h-16 text-gray-400" />
              </div>
            )}
          </div>
          <label
            htmlFor="profile_picture_input"
            className="absolute bottom-0 right-0 p-2 bg-primary-600 rounded-full text-white cursor-pointer hover:bg-primary-700 transition-all shadow-lg transform group-hover:scale-110"
          >
            <Upload className="w-5 h-5" />
          </label>
          <input
            id="profile_picture_input"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = () => {
                  setTempImageUrl(reader.result);
                  setIsCropperOpen(true);
                };
                reader.readAsDataURL(file);
                e.target.value = '';
              }
            }}
          />
        </div>
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
          {formData.profile_picture_filename
            ? `Fichier sélectionné : ${formData.profile_picture_filename}`
            : "Choisissez une photo de profil ou un logo pour votre entreprise."}
        </p>
      </div>
    </div>
  );

  const renderStepContent = () => {
    if (isLoading && currentStep === 0 && !error) {
      return (
        <div className="flex justify-center items-center h-full min-h-[300px]">
          <Loader2 className="w-12 h-12 animate-spin text-primary-500" />
        </div>
      );
    }
    switch (currentStep) {
      case 0:
        return renderCompanyInfo();
      case 1:
        return renderSocialLinks();
      case 2:
        return renderProfilePicture();
      default:
        return <p>Étape inconnue ou chargement...</p>;
    }
  };

  if (isLoading && !user?.id && !error) {
    return (
      <div className="w-full min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 py-8">
        <Loader2 className="w-16 h-16 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!isAuthenticated && !isLoading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4 py-8">
        <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-lg shadow-xl">
          <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-4">
            Accès non autorisé
          </h2>
          <p className="text-xl text-gray-700 dark:text-gray-300 mb-6">
            {error || "Veuillez vous connecter pour compléter votre profil."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 dark:bg-gray-900 px-4 py-8">
      <div className="w-full max-w-4xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Complétez votre profil
          </h1>
          {steps[currentStep] && (
            <p className="text-gray-600 dark:text-gray-300 text-md sm:text-lg">
              Étape {currentStep + 1} sur {steps.length}:{" "}
              {steps[currentStep].title}
            </p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6 mb-8">
          <div className="flex items-start justify-between w-full">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isCompletedByAPI = isStepCompleted(index);
              const isPast = index < currentStep;
              return (
                <React.Fragment key={step.id}>
                  <div
                    className={`flex flex-col items-center text-center flex-1 ${isCompletedByAPI || isPast || isActive
                      ? "cursor-pointer"
                      : "cursor-default"
                      }`}
                    onClick={() =>
                      (isCompletedByAPI || isPast || isActive) && !isLoading
                        ? setCurrentStep(index)
                        : null
                    }
                  >
                    <div
                      className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 transition-all duration-300 ${isCompletedByAPI
                        ? "bg-green-500 border-green-500 text-white shadow-md"
                        : isActive
                          ? "bg-primary-500 border-primary-500 text-white shadow-md ring-2 sm:ring-4 ring-primary-200 dark:ring-primary-700"
                          : isPast
                            ? "bg-gray-300 dark:bg-gray-600 border-gray-400 dark:border-gray-500 text-gray-700 dark:text-gray-300"
                            : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500"
                        }`}
                    >
                      {isCompletedByAPI ? (
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                      ) : (
                        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                      )}
                    </div>
                    <p
                      className={`mt-2 text-xs sm:text-sm font-medium transition-colors duration-200 w-full px-1 break-words ${isActive
                        ? "text-primary-600 dark:text-primary-400 font-semibold"
                        : isCompletedByAPI
                          ? "text-green-600 dark:text-green-400"
                          : "text-gray-500 dark:text-gray-400"
                        }`}
                    >
                      {step.title}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mt-5 sm:mt-6 mx-1 sm:mx-2 rounded-full transition-all duration-300 ${(isCompletedByAPI &&
                        (isStepCompleted(index + 1) ||
                          isActive ||
                          currentStep > index + 1)) ||
                        (isActive && isStepCompleted(index))
                        ? "bg-green-500"
                        : isPast && currentStep > index
                          ? "bg-primary-500"
                          : "bg-gray-300 dark:bg-gray-600"
                        }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        {error && (
          <div
            className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md relative mb-6 shadow-md"
            role="alert"
          >
            <strong className="font-bold block">Erreur:</strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8 min-h-[350px] sm:min-h-[400px]">
          {renderStepContent()}
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-center bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0 || isLoading}
            className={`flex items-center justify-center px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg font-medium transition-all duration-200 w-full sm:w-auto mb-3 sm:mb-0 disabled:opacity-50 disabled:cursor-not-allowed ${currentStep === 0
              ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
              : "bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-500 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              }`}
          >
            <ChevronLeft className="w-5 h-5 mr-2" /> Précédent
          </button>
          {currentStep === steps.length - 1 ? (
            <button
              onClick={handleFinalSubmit}
              disabled={!canProceedToNext() || isLoading}
              className={`flex items-center justify-center px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg font-medium transition-all duration-200 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed ${!canProceedToNext() || isLoading
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
                : "bg-green-500 dark:bg-green-600 text-white hover:bg-green-600 dark:hover:bg-green-500 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                }`}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : null}
              Terminer
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceedToNext() || isLoading}
              className={`flex items-center justify-center px-6 py-2.5 sm:px-8 sm:py-3 rounded-lg font-medium transition-all duration-200 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed ${!canProceedToNext() || isLoading
                ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
                : "bg-primary-500 dark:bg-primary-600 text-white hover:bg-primary-600 dark:hover:bg-primary-500 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                }`}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : null}
              Suivant <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          )}
        </div>
      </div>

      <ImageCropperModal
        isOpen={isCropperOpen}
        image={tempImageUrl}
        onCropComplete={(croppedBlob) => {
          const previewUrl = URL.createObjectURL(croppedBlob);
          setAvatarPreview(previewUrl);
          const file = new File([croppedBlob], "avatar.jpg", { type: "image/jpeg" });
          handleInputChange("profile_picture", file);
          setIsCropperOpen(false);
          setTempImageUrl(null);
        }}
        onCancel={() => {
          setIsCropperOpen(false);
          setTempImageUrl(null);
        }}
      />
    </div>
  );
};

export default RecruiterCompleteProfile;
